import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Preferences } from '@capacitor/preferences';
import { FeedPostType, FeedFilterState, DEFAULT_FEED_FILTER } from '../../types/feed';
import { RootState } from '../index';
import logger from '../../utils/logger';

const STORAGE_KEY = 'feed_filter_preferences';

/**
 * Load feed filter preferences from local storage
 */
export const loadFeedFilters = createAsyncThunk(
  'feedFilter/load',
  async () => {
    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      if (value) {
        const parsed = JSON.parse(value);
        logger.info('[feedFilter] Loaded filter preferences:', parsed);
        return parsed as FeedPostType[];
      }
      logger.info('[feedFilter] No saved preferences, using defaults');
      return DEFAULT_FEED_FILTER;
    } catch (error) {
      logger.error('[feedFilter] Error loading preferences:', error);
      return DEFAULT_FEED_FILTER;
    }
  }
);

/**
 * Save feed filter preferences to local storage
 */
export const saveFeedFilters = createAsyncThunk(
  'feedFilter/save',
  async (enabledTypes: FeedPostType[]) => {
    try {
      await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(enabledTypes) });
      logger.info('[feedFilter] Saved filter preferences:', enabledTypes);
      return enabledTypes;
    } catch (error) {
      logger.error('[feedFilter] Error saving preferences:', error);
      throw error;
    }
  }
);

const initialState: FeedFilterState = {
  enabledTypes: DEFAULT_FEED_FILTER,
  isLoaded: false
};

const feedFilterSlice = createSlice({
  name: 'feedFilter',
  initialState,
  reducers: {
    /**
     * Toggle a specific post type on/off
     */
    togglePostType: (state, action: PayloadAction<FeedPostType>) => {
      const type = action.payload;
      if (state.enabledTypes.includes(type)) {
        state.enabledTypes = state.enabledTypes.filter(t => t !== type);
      } else {
        state.enabledTypes = [...state.enabledTypes, type];
      }
    },
    /**
     * Set all enabled types at once
     */
    setEnabledTypes: (state, action: PayloadAction<FeedPostType[]>) => {
      state.enabledTypes = action.payload;
    },
    /**
     * Reset to default filter settings
     */
    resetToDefault: (state) => {
      state.enabledTypes = [...DEFAULT_FEED_FILTER];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFeedFilters.fulfilled, (state, action) => {
        state.enabledTypes = action.payload;
        state.isLoaded = true;
      })
      .addCase(saveFeedFilters.fulfilled, (state, action) => {
        state.enabledTypes = action.payload;
      });
  }
});

// Export actions
export const { togglePostType, setEnabledTypes, resetToDefault } = feedFilterSlice.actions;

// Selectors
export const selectFeedFilterState = (state: RootState) => state.feedFilter;
export const selectEnabledTypes = (state: RootState) => state.feedFilter.enabledTypes;
export const selectIsFilterLoaded = (state: RootState) => state.feedFilter.isLoaded;

/**
 * Check if a specific post type is enabled
 */
export const selectIsTypeEnabled = (state: RootState, postType: FeedPostType) =>
  state.feedFilter.enabledTypes.includes(postType);

/**
 * Get count of hidden post types (for badge display)
 */
export const selectHiddenTypesCount = (state: RootState) => {
  const allTypes: FeedPostType[] = [
    'training', 'competition', 'promotion', 'beltPromotion',
    'exercise', 'workoutSession', 'story', 'topic'
  ];
  return allTypes.length - state.feedFilter.enabledTypes.length;
};

export default feedFilterSlice.reducer;
